/**
 * Editorial-Swiss chart defaults: no chartjunk, hairline y-grid, no x-grid,
 * muted axis labels. All colours come from the live palette (see useChartPalette).
 */
import { formatCurrency } from '../../utils/formatters.js';

function legendTop(palette) {
  return { display: true, position: 'top', labels: { color: palette.axis, boxWidth: 10, boxHeight: 10, usePointStyle: true } };
}

/** Axis tick label for money axes: compact ('12k') to keep the axis quiet. */
function compactAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  if (Math.abs(n) >= 1000) {
    return `${(n / 1000).toLocaleString('de-CH', { maximumFractionDigits: 1 })}k`;
  }
  return n.toLocaleString('de-CH');
}

/**
 * y-axis config per value kind: 'count' forces integer ticks (no 0.4 invoices),
 * 'currency' renders compact amounts. Default keeps Chart.js auto behaviour.
 */
function valueScale(palette, valueKind) {
  const base = { ticks: { color: palette.axis }, grid: { color: palette.grid } };
  if (valueKind === 'count') base.ticks.precision = 0;
  if (valueKind === 'currency') base.ticks.callback = (v) => compactAmount(v);
  return base;
}

function tooltipFor(valueKind) {
  const base = { mode: 'index', intersect: false };
  if (valueKind === 'currency') {
    base.callbacks = { label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y ?? ctx.parsed)}` };
  }
  return base;
}

export function baseLineOptions(palette, { valueKind } = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'index' },
    plugins: { legend: legendTop(palette), tooltip: tooltipFor(valueKind) },
    scales: {
      x: { ticks: { color: palette.axis }, grid: { display: false } },
      y: valueScale(palette, valueKind),
    },
  };
}

export function baseBarOptions(palette, { stacked = false, valueKind } = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: legendTop(palette), tooltip: tooltipFor(valueKind) },
    scales: {
      x: { stacked, ticks: { color: palette.axis }, grid: { display: false } },
      y: { stacked, ...valueScale(palette, valueKind) },
    },
  };
}

export function baseDonutOptions(palette) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%',
    plugins: { legend: { display: true, position: 'right', labels: { color: palette.axis, boxWidth: 10, boxHeight: 10, usePointStyle: true } } },
  };
}
